import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import AccountAPI from '../../api/account';
import EnterpriseAccountAPI from '../../api/enterprise/account';
import { throwErrorMessage } from '../utils/api';
import { FEATURE_FLAGS } from '../../featureFlags';

const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isUpdating: false,
    isCheckoutInProcess: false,
  },
};

export const getters = {
  getAccount: ($state) => (id) => {
    return $state.records.find((record) => record.id === Number(id)) || {};
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  isFeatureEnabledonAccount:
    ($state, _, __, rootGetters) => (id, featureName) => {
      // If a user is SuperAdmin and has access to the account, then they would see all the available features
      const isUserASuperAdmin =
        rootGetters.getCurrentUser?.type === 'SuperAdmin';

      if (isUserASuperAdmin) {
        return true;
      }

      const { features = {} } =
        $state.records.find((record) => record.id === Number(id)) || {};

      const chatlynFeatures = {
        [FEATURE_FLAGS.AUTOMATION_STUDIO]: true,
        [FEATURE_FLAGS.MESSAGE_TEMPLATES]: true,
      };

      const allFeatures = { ...features, ...chatlynFeatures };
      return allFeatures[featureName] || false;
    },
  getPartner: (_, $getters, __, rootGetters) => {
    const accountId = rootGetters.getCurrentAccountId;
    const currentAccount = $getters.getAccount(accountId);
    return currentAccount?.custom_attributes?.partner;
  },
  getFeatureSet: (_, $getters, __, rootGetters) => {
    const accountId = rootGetters.getCurrentAccountId;
    const currentAccount = $getters.getAccount(accountId);
    return currentAccount?.custom_attributes?.featureSet;
  },
  isPhoenix: (_, $getters, __, rootGetters) => {
    const accountId = rootGetters.getCurrentAccountId;
    const currentAccount = $getters.getAccount(accountId);
    return currentAccount?.custom_attributes?.featureSet === 'PHOENIX';
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await AccountAPI.get();

      commit(types.default.ADD_ACCOUNT, response.data);
      commit(types.default.SET_ACCOUNT_UI_FLAG, {
        isFetchingItem: false,
      });
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, {
        isFetchingItem: false,
      });
    }
  },
  setAccountsFromLogin: ({ commit }, accounts) => {
    // Populate accounts store immediately with data from login response
    // This allows immediate phoenix detection without waiting for accounts/get
    accounts.forEach((account) => {
      commit(types.default.ADD_ACCOUNT, account);
    });
  },
  update: async ({ commit }, updateObj) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: true });
    try {
      await AccountAPI.update('', updateObj);
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
      throw new Error(error);
    }
  },
  create: async ({ commit }, accountInfo) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCreating: true });
    try {
      const response = await AccountAPI.createAccount(accountInfo);
      const account_id = response.data.data.account_id;
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCreating: false });
      return account_id;
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCreating: false });
      throw error;
    }
  },

  checkout: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: true });
    try {
      const response = await EnterpriseAccountAPI.checkout();
      window.location = response.data.redirect_url;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: false });
    }
  },

  subscription: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: true });
    try {
      await EnterpriseAccountAPI.subscription();
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: false });
    }
  },

  limits: async ({ commit }) => {
    try {
      const response = await EnterpriseAccountAPI.getLimits();
      commit(types.default.SET_ACCOUNT_LIMITS, response.data);
    } catch (error) {
      // silent error
    }
  },

  getCacheKeys: async () => {
    return AccountAPI.getCacheKeys();
  },

  setAccount: async ({ dispatch }, accountId) => {
    try {
      await dispatch('get');
      await dispatch('setActiveAccount', { accountId }, { root: true });
    } catch {
      // silent error
    }
  },
};

export const mutations = {
  [types.default.SET_ACCOUNT_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.ADD_ACCOUNT]: MutationHelpers.setSingleRecord,
  [types.default.EDIT_ACCOUNT]: MutationHelpers.update,
  [types.default.SET_ACCOUNT_LIMITS]: MutationHelpers.updateAttributes,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
